import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { Prescription } from '../../../core/models/prescription.model';
import { ClientReferralTask } from '../../../core/models/client-referral-task.model';
import { Drug } from '../../../core/models/drug.model';
import { PrescriptionFamily } from '../../../core/models/prescription-family.model';
import { handleSimpleChanges } from '../../../core/lib/component-utils';
import { ClientReferralTasksService } from '../../../core/services/client-referral-tasks.service';
import { initRejection } from '../client-task-reject-prescription/client-task-reject-prescription.component';
import { TaskSubmission } from '../../../core/models/task-submission.model';
import { SlicePipe, DatePipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ClientTaskSubmittedPrescriptionComponent } from '../client-task-submitted-prescription/client-task-submitted-prescription.component';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';

class PrescriptionOption {
  constructor(
    public id: number,
    public drugs: Drug[],
    public prescriptionFamily: PrescriptionFamily,
    public writtenDate: Date,
    public verified: boolean,
    public rejected: boolean,
    public showWarning: boolean,
    public submission: TaskSubmission,
    public verifications: { valid: boolean; referralDate: Date }[],
    public rejection: { checked: boolean; disabled: boolean }
  ) {}

  get submitted() {
    return this.verified || this.rejected;
  }
}

const initVerifications = (prescription: Prescription): { valid: boolean; referralDate: Date }[] => {
  if (prescription.clientRejected || prescription.allClientReferralVerificationsInvalid) {
    return [];
  }

  return prescription.validatedClientReferralVerifications.map(
    crv => ({ valid: crv.valid, referralDate: crv.verification.referralDate })
  );
};

@Component({
    selector: 'app-client-referral-task-prescriptions',
    templateUrl: './client-referral-task-prescriptions.component.html',
    styleUrls: ['./client-referral-task-prescriptions.component.scss'],
    imports: [FaIconComponent, ClientTaskSubmittedPrescriptionComponent, SlicePipe, DatePipe, JoinListPipe]
})
export class ClientReferralTaskPrescriptionsComponent implements OnChanges {
  private clientReferralTasksService = inject(ClientReferralTasksService);

  @Input() task: ClientReferralTask;
  @Input() prescriptions: Prescription[];
  @Input() canAddVerifications: boolean;
  @Input() formDisabled: boolean;

  @Output() addVerification = new EventEmitter();
  @Output() taskChange = new EventEmitter<ClientReferralTask>();

  prescriptionOptions: PrescriptionOption[] = [];

  ngOnChanges(changes: SimpleChanges) {
    handleSimpleChanges(changes, (inputName: string) => {
      if (inputName === 'task' || inputName === 'prescriptions') {
        this.initPrescriptionOptions();
      }
    });
  }

  onPrescriptionRejectionChange({ prescriptionId, checked }: { prescriptionId: number; checked: boolean }) {
    const { id } = this.task;
    let modifiedPrescriptionIds = [...this.task.rejectedPrescriptionIds];

    if (checked) {
      modifiedPrescriptionIds.push(prescriptionId);
    } else {
      modifiedPrescriptionIds = modifiedPrescriptionIds.filter(pId => pId !== prescriptionId);
    }

    this.clientReferralTasksService
      .updateRejectedPrescription(id, modifiedPrescriptionIds)
      .subscribe((task: ClientReferralTask) => {
        this.taskChange.emit(task);
      });
  }

  onAddReferralClick($event) {
    $event.preventDefault();
    this.addVerification.emit();
  }

  private initPrescriptionOptions() {
    this.prescriptionOptions = this.prescriptions.map(prescription => new PrescriptionOption(
        prescription.id,
        prescription.drugs,
        prescription.prescriptionFamily,
        prescription.writtenDate,
        !!prescription.submittedClientReferralVerification,
        prescription.clientRejected,
        prescription.allClientReferralVerificationsInvalid,
        prescription.clientReferralTaskSubmission,
        initVerifications(prescription),
        initRejection(this.task, prescription)
      ));
  }
}
